import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { AXIS, LINE_COLORS } from './chartTheme';
import { tipFormatter, AreaGradient } from './ChartKit';
import {
  Line, XAxis, YAxis, CartesianGrid, ReferenceLine, ComposedChart, Bar, Area
} from 'recharts';

/**
 * Seccion Ventas rehecha: KPIs con delta, curva actual vs periodo anterior con media
 * (series activables), ventas por linea de negocio, ranking unificado de clientes
 * y presupuestos sin convertir.
 */

const money = (n: number) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n).toLocaleString('es-AR')}`;
};

function DeltaChip({ value }: { value: number | null }) {
  if (value == null) return <span className="text-xs text-muted-foreground">sin ref.</span>;
  const color = value > 0 ? 'text-green-600 bg-green-500/10' : value < 0 ? 'text-red-600 bg-red-500/10' : 'text-muted-foreground bg-muted';
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded ${color}`}>
      {value > 0 ? '▲' : value < 0 ? '▼' : ''} {value > 0 ? '+' : ''}{value}% vs per. ant.
    </span>
  );
}

function Kpi({ label, value, delta, sub }: { label: string; value: string; delta?: number | null; sub?: string }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className="text-2xl font-semibold leading-none mb-1.5">{value}</p>
        {delta !== undefined ? <DeltaChip value={delta} /> : sub ? <span className="text-xs text-muted-foreground">{sub}</span> : null}
      </CardContent>
    </Card>
  );
}

const DIAG_ESTADOS: Record<string, string> = {
  open: 'Abiertos', delivered: 'Entregados', budgeted: 'Presupuestados',
  approved: 'Aprobados', closed: 'Cerrados', not_converted: 'No convertidos'
};

/** Embudo comercial: diagnostico -> presupuesto -> aprobado -> venta, con motivos de caida. */
function EmbudoBlock({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    axios.get('/api/reports/tablero/embudo', { params: { startDate, endDate } })
      .then((r) => setD(r.data))
      .catch(() => setD(null));
  }, [startDate, endDate]);
  if (!d) return null;

  const p = d.presupuestos;
  const stages = [
    { label: 'Diagnósticos', n: d.diagnosticos.total, sub: null as string | null, rate: null as number | null },
    { label: 'Presupuestos emitidos', n: p.emitidos.n, sub: money(p.emitidos.monto), rate: d.diagnosticos.total ? d.diagnosticos.tasaPresupuestacion : null },
    { label: 'Aprobados', n: p.aprobados.n, sub: money(p.aprobados.monto), rate: p.tasaAprobacion },
    { label: 'Convertidos en venta', n: p.convertidos.n, sub: money(p.convertidos.monto), rate: p.tasaConversion }
  ];
  const maxCat = Math.max(1, ...d.categorias.map((c: any) => c.n));

  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
          <h3 className="font-semibold text-sm">Embudo comercial: del diagnóstico a la venta</h3>
          <span className="text-xs text-muted-foreground">Cohorte creada en el período · estado actual</span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
          {stages.map((s, i) => (
            <div key={s.label} className="rounded-lg border p-3 relative">
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className="text-2xl font-semibold leading-tight">{s.n}</p>
              <p className="text-xs text-muted-foreground h-4">{s.sub || ''}</p>
              {i > 0 && s.rate != null && (
                <span className={`absolute top-2 right-2 text-xs px-1.5 py-0.5 rounded ${s.rate >= 50 ? 'text-green-600 bg-green-500/10' : s.rate >= 25 ? 'text-amber-600 bg-amber-500/10' : 'text-red-600 bg-red-500/10'}`}>
                  {s.rate}%
                </span>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          {p.pendientes.n > 0 && <span className="px-2 py-1 rounded bg-muted text-muted-foreground">{p.pendientes.n} pendientes de respuesta ({money(p.pendientes.monto)})</span>}
          {p.rechazados.n > 0 && <span className="px-2 py-1 rounded bg-red-500/10 text-red-600">{p.rechazados.n} rechazados ({money(p.rechazados.monto)})</span>}
          {p.vencidos.n > 0 && <span className="px-2 py-1 rounded bg-amber-500/10 text-amber-600">{p.vencidos.n} vencidos ({money(p.vencidos.monto)})</span>}
          {p.desdeDiagnostico > 0 && <span className="px-2 py-1 rounded bg-blue-500/10 text-blue-600">{p.desdeDiagnostico} presupuestos nacieron de un diagnóstico</span>}
        </div>

        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-2">
            <h4 className="text-xs font-medium text-muted-foreground">¿Cómo venimos convirtiendo? · últimos 12 meses</h4>
            <div className="flex gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm" style={{ background: 'hsl(var(--chart-1))' }} />Emitidos</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm" style={{ background: 'hsl(var(--chart-2))' }} />Convertidos</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 rounded" style={{ background: 'hsl(var(--chart-4))' }} />% conversión</span>
            </div>
          </div>
          <ChartContainer
            config={{
              emitidos: { label: 'Emitidos', color: 'hsl(var(--chart-1))' },
              convertidos: { label: 'Convertidos', color: 'hsl(var(--chart-2))' },
              tasa: { label: '% conversión', color: 'hsl(var(--chart-4))' }
            }}
            className="h-[220px] w-full"
          >
            <ComposedChart data={d.serie12} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
              <XAxis dataKey="label" {...AXIS} />
              <YAxis yAxisId="left" allowDecimals={false} {...AXIS} width={30} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} {...AXIS} tickFormatter={(v: number) => `${v}%`} width={38} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(v: any, name: any, item: any) => {
                      const label = name === 'tasa' ? '% conversión' : name === 'emitidos' ? 'Emitidos' : 'Convertidos';
                      const val = name === 'tasa'
                        ? `${v}%`
                        : `${v} (${money(name === 'emitidos' ? (item?.payload?.montoEmitido ?? 0) : (item?.payload?.montoConvertido ?? 0))})`;
                      return (
                        <>
                          <span className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ background: item?.color || item?.payload?.fill }} />
                          <div className="flex flex-1 items-center justify-between gap-3 leading-none">
                            <span className="text-muted-foreground">{label}</span>
                            <span className="font-mono font-medium tabular-nums text-foreground">{val}</span>
                          </div>
                        </>
                      );
                    }}
                  />
                }
              />
              <Bar yAxisId="left" dataKey="emitidos" fill="var(--color-emitidos)" radius={[3, 3, 0, 0]} />
              <Bar yAxisId="left" dataKey="convertidos" fill="var(--color-convertidos)" radius={[3, 3, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="tasa" stroke="var(--color-tasa)" strokeWidth={2} dot={{ r: 2.5 }} connectNulls />
            </ComposedChart>
          </ChartContainer>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-2">¿Por qué se rechazan?</h4>
            {d.motivosRechazo.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin rechazos en el período.</p>
            ) : (
              <div className="space-y-1.5">
                {d.motivosRechazo.map((m: any) => (
                  <div key={m.motivo} className="flex items-center justify-between text-sm">
                    <span>{m.motivo}</span>
                    <span className="font-medium">{m.n}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-2">¿Qué falla más? (incidencias de diagnósticos)</h4>
            {d.categorias.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Sin diagnósticos con incidencias en el período — a medida que se carguen, acá vas a ver qué categorías conviene empujar.
              </p>
            ) : (
              <div className="space-y-2">
                {d.categorias.map((c: any) => (
                  <div key={c.categoria}>
                    <div className="flex items-baseline justify-between text-sm mb-0.5">
                      <span>{c.categoria}</span>
                      <span className="text-xs text-muted-foreground">
                        {c.n} {c.graves > 0 && <span className="text-red-600">· {c.graves} grave{c.graves > 1 ? 's' : ''}</span>}
                      </span>
                    </div>
                    <div className="w-full h-1.5 rounded bg-muted overflow-hidden">
                      <div className="h-full rounded bg-foreground/70" style={{ width: `${(c.n / maxCat) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {d.diagnosticos.porEstado.length > 0 && (
          <p className="text-xs text-muted-foreground mt-4">
            Diagnósticos del período: {d.diagnosticos.porEstado.map((e: any) => `${DIAG_ESTADOS[e.status] || e.status} ${e.n}`).join(' · ')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function VentasSection({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [show, setShow] = useState({ actual: true, anterior: true, media: true });
  const [showListaPend, setShowListaPend] = useState(false);

  useEffect(() => {
    if (!startDate || !endDate) return;
    setError('');
    setLoading(true);
    axios.get('/api/reports/tablero/ventas', { params: { startDate, endDate } })
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando ventas'))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading && !d) return <div className="py-10 text-center text-muted-foreground">Cargando ventas...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  const maxLinea = Math.max(1, ...d.porLinea.map((l: any) => l.revenue));

  return (
    <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        <Kpi label="Facturación" value={money(d.kpis.facturacion.value)} delta={d.kpis.facturacion.deltaPct} />
        <Kpi label="Ventas (remitos)" value={String(d.kpis.ventas.value)} delta={d.kpis.ventas.deltaPct} />
        <Kpi label="Ticket promedio" value={money(d.kpis.ticket.value)} delta={d.kpis.ticket.deltaPct} />
        <Kpi label="Descuentos otorgados" value={money(d.kpis.descuentos?.total ?? 0)} sub={`en ${d.kpis.descuentos?.ventas ?? 0} ventas`} />
        <Kpi label="En presupuestos sin convertir" value={money(d.kpis.sinConvertir.value)} sub={`${d.kpis.sinConvertir.count} presupuestos esperando`} />
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-semibold text-sm">Evolución de ventas</h3>
            <div className="flex gap-3 text-xs">
              {[
                { k: 'actual', label: 'Período actual', color: 'hsl(var(--chart-2))' },
                { k: 'anterior', label: 'Período anterior', color: 'hsl(var(--muted-foreground))' },
                { k: 'media', label: 'Media', color: '#378ADD' }
              ].map((s) => (
                <button
                  key={s.k}
                  onClick={() => setShow((p) => ({ ...p, [s.k]: !(p as any)[s.k] }))}
                  className={`flex items-center gap-1.5 ${!(show as any)[s.k] ? 'opacity-35' : ''}`}
                >
                  <span className="inline-block w-3 h-0.5 rounded" style={{ background: s.color }} />
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <ChartContainer
            config={{
              actual: { label: 'Período actual', color: 'hsl(var(--chart-2))' },
              anterior: { label: 'Período anterior', color: 'hsl(var(--muted-foreground))' }
            }}
            className="h-[260px] w-full"
          >
            <ComposedChart data={d.serie} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <defs>
                <AreaGradient id="fillVentasActual" color="var(--color-actual)" />
              </defs>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} vertical={false} />
              <XAxis dataKey="label" {...AXIS} />
              <YAxis {...AXIS} tickFormatter={(v: number) => money(v)} width={56} />
              <ChartTooltip content={<ChartTooltipContent formatter={tipFormatter({ actual: 'Período actual', anterior: 'Período anterior' }, money)} />} />
              {show.media && d.media > 0 && (
                <ReferenceLine y={d.media} stroke="#378ADD" strokeDasharray="4 4" label={{ value: `media ${money(d.media)}`, fontSize: 10, fill: '#378ADD', position: 'insideTopRight' }} />
              )}
              {show.anterior && <Line type="monotone" dataKey="anterior" stroke="var(--color-anterior)" strokeWidth={1.4} strokeDasharray="5 4" dot={false} />}
              {show.actual && <Area type="monotone" dataKey="actual" stroke="var(--color-actual)" strokeWidth={2.4} fill="url(#fillVentasActual)" dot={{ r: 2 }} />}
            </ComposedChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Ventas por línea de negocio</h3>
            {d.porLinea.length === 0 && <p className="text-sm text-muted-foreground">Sin ventas en el período</p>}
            <div className="space-y-2.5">
              {d.porLinea.map((l: any) => (
                <div key={l.area}>
                  <div className="flex items-baseline justify-between text-sm mb-1">
                    <span className="font-medium">{l.area}</span>
                    <span>
                      {money(l.revenue)}
                      {l.deltaPct != null && (
                        <span className={`text-xs ml-2 ${l.deltaPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {l.deltaPct > 0 ? '+' : ''}{l.deltaPct}%
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="w-full h-2 rounded bg-muted overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${(l.revenue / maxLinea) * 100}%`, background: LINE_COLORS[l.area] || '#98968f' }} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-3">Ranking de clientes del período</h3>
            {d.ranking.length === 0 && <p className="text-sm text-muted-foreground">Sin ventas en el período</p>}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground text-left">
                  <th className="pb-1.5 font-normal">Cliente</th>
                  <th className="pb-1.5 font-normal text-right">Facturado</th>
                  <th className="pb-1.5 font-normal text-right">% acum.</th>
                  <th className="pb-1.5 font-normal text-right">Última compra</th>
                </tr>
              </thead>
              <tbody>
                {d.ranking.map((c: any, i: number) => (
                  <tr key={i} className="border-t">
                    <td className="py-1.5 pr-2 truncate max-w-[160px]">{c.name}</td>
                    <td className="py-1.5 pr-2 text-right font-medium whitespace-nowrap">{money(c.revenue)}</td>
                    <td className="py-1.5 pr-2 text-right text-muted-foreground">{c.pctAcum}%</td>
                    <td className="py-1.5 text-right text-muted-foreground whitespace-nowrap">{c.ultima ? new Date(c.ultima + 'T12:00:00').toLocaleDateString('es-AR') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {d.ranking.length > 0 && d.ranking[Math.min(2, d.ranking.length - 1)].pctAcum >= 70 && (
              <p className="text-xs text-amber-600 mt-2">⚠ Los primeros {Math.min(3, d.ranking.length)} clientes concentran {d.ranking[Math.min(2, d.ranking.length - 1)].pctAcum}% de la facturación del período.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <EmbudoBlock startDate={startDate} endDate={endDate} />

      <Card>
        <CardContent className="pt-5">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-semibold text-sm">Presupuestos sin convertir</h3>
            <span className="text-sm text-muted-foreground">{money(d.kpis.sinConvertir.value)} sobre la mesa</span>
          </div>
          {d.presupuestos.length === 0 && <p className="text-sm text-muted-foreground">No hay presupuestos pendientes de conversión. 🎉</p>}
          {d.presupuestos.length > 0 && (() => {
            const BUCKETS = [
              { label: 'Frescos (0-7 días)', min: 0, max: 7, color: '#1baf7a' },
              { label: 'Para empujar (8-21 días)', min: 8, max: 21, color: '#378ADD' },
              { label: 'Fríos (22-60 días)', min: 22, max: 60, color: '#EF9F27' },
              { label: 'Casi perdidos (+60 días)', min: 61, max: Infinity, color: '#E24B4A' }
            ].map((b) => {
              const items = d.presupuestos.filter((p: any) => p.dias >= b.min && p.dias <= b.max);
              return { ...b, n: items.length, monto: items.reduce((a: number, p: any) => a + p.total, 0) };
            });
            const maxMonto = Math.max(1, ...BUCKETS.map((b) => b.monto));
            return (
              <>
                <p className="text-xs text-muted-foreground mb-2">Cuanto más viejo, menos chance de convertirse: la plata está en llamar a los del medio antes de que se enfríen.</p>
                <div className="space-y-2.5 mb-3">
                  {BUCKETS.map((b) => (
                    <div key={b.label}>
                      <div className="flex items-baseline justify-between text-sm mb-1">
                        <span>{b.label}</span>
                        <span className="font-medium">
                          {money(b.monto)} <span className="text-xs text-muted-foreground">· {b.n} presup.</span>
                        </span>
                      </div>
                      <div className="w-full h-2 rounded bg-muted overflow-hidden">
                        <div className="h-full rounded" style={{ width: `${(b.monto / maxMonto) * 100}%`, background: b.color }} />
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  onClick={() => setShowListaPend((v) => !v)}
                >
                  {showListaPend ? 'Ocultar lista' : `Ver la lista (${d.presupuestos.length})`}
                </button>
                {showListaPend && (
                  <div className="overflow-x-auto max-h-72 overflow-y-auto mt-2">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-muted-foreground text-left">
                          <th className="pb-1.5 font-normal">Número</th>
                          <th className="pb-1.5 font-normal">Cliente</th>
                          <th className="pb-1.5 font-normal text-right">Monto</th>
                          <th className="pb-1.5 font-normal text-right">Antigüedad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.presupuestos.map((p: any) => (
                          <tr key={p.id} className="border-t">
                            <td className="py-1.5 pr-2">{p.numero || p.id.slice(0, 8)}</td>
                            <td className="py-1.5 pr-2 truncate max-w-[220px]">{p.cliente}</td>
                            <td className="py-1.5 pr-2 text-right font-medium">{money(p.total)}</td>
                            <td className={`py-1.5 text-right ${p.dias > 21 ? 'text-red-600' : p.dias > 10 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {p.dias} días
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
